import { useEffect, useState, useCallback } from "react";
import { Inbox, Clock, CheckCircle, XCircle, RefreshCw, Loader2, Bell, Lock, Users } from "lucide-react";
import { getDealsByDealer, updateDealStatus, DealRecord } from "@/Calls/callDeals";
import { getLenders, assignSalesperson, deskDeal, LenderOption } from "@/Calls/callLenders";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { socket } from "@/lib/socket";
import { DealSocketPayload } from "@/types/socketEvents";
import { API_URL } from "@/config";
import axios from "axios";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

interface SalesPerson { salesperson_id: number; salesperson_firstname: string | null; salesperson_lastname: string | null; }

const STATUS_COLORS: Record<string, string> = {
    PENDING:   "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
    VIEWED:    "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    COUNTERED: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400",
    ACCEPTED:  "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
    DECLINED:  "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    FUNDED:    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
    CANCELLED: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

function fmt(v: string | null | undefined) {
    if (!v) return "—";
    const n = Number(v);
    if (isNaN(n)) return v;
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface CounterState {
    apr: string;
    term: string;
    downPayment: string;
    monthlyPayment: string;
    amountFinanced: string;
}

const emptyCounter = (): CounterState => ({
    apr: "", term: "", downPayment: "", monthlyPayment: "", amountFinanced: "",
});

export default function DealerDealDeskPage() {
    const { user } = useAuth();
    const dealerRoom = (user as any)?.dealerRoom ?? (user as any)?.room ?? "";
    const dealerId = (user as any)?.dealerId ?? (user as any)?.id ?? 0;

    const [deals, setDeals] = useState<DealRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [newIds, setNewIds] = useState<Set<number>>(new Set());
    const [expanded, setExpanded] = useState<number | null>(null);
    const [counters, setCounters] = useState<Record<number, CounterState>>({});
    const [acting, setActing] = useState<number | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>("PENDING");

    // Deal desk extras
    const [lenders, setLenders] = useState<LenderOption[]>([]);
    const [salespeople, setSalespeople] = useState<SalesPerson[]>([]);
    const [deskPanel, setDeskPanel] = useState<number | null>(null);
    const [deskState, setDeskState] = useState<Record<number, { sp1Id?: number; sp1Name?: string; sp2Id?: number; sp2Name?: string; lenderId?: number; locking: boolean }>>({});

    // Load lenders + staff once
    useEffect(() => {
        getLenders().then(setLenders).catch(() => {});
        if (dealerId) {
            http.get<SalesPerson[]>(`/sales/auth/staff`, { params: { dealerId } })
                .then(r => setSalespeople(r.data))
                .catch(() => {});
        }
    }, [dealerId]);

    // Initial load
    useEffect(() => {
        if (!dealerId) return;
        (async () => {
            try {
                const data = await getDealsByDealer(Number(dealerId), statusFilter !== "ALL" ? statusFilter : undefined);
                setDeals(data);
            } catch { /* silent */ } finally {
                setLoading(false);
            }
        })();
    }, [dealerId, statusFilter]);

    // Real-time: join dealer room & listen for new/updated deals
    useEffect(() => {
        if (!dealerRoom) return;
        socket.emit("joinDealer", { room: dealerRoom, role: "dealer" });

        const onNewDeal = (payload: DealSocketPayload) => {
            // Prepend if not already present
            setDeals(prev => {
                if (prev.find(d => d.deal_id === payload.dealId)) return prev;
                const newDeal: DealRecord = {
                    deal_id: payload.dealId,
                    deal_token: payload.dealToken,
                    deal_number: 0,
                    deal_dealerID: Number(dealerId),
                    deal_status: "PENDING",
                    deal_type: payload.dealType ?? "FINANCE",
                    vehicle_id: payload.vehicleId ?? null,
                    customer_id: payload.customerId ?? null,
                    vYear: payload.vehicleYear ?? null,
                    vMake: payload.vehicleMake ?? null,
                    vModel: payload.vehicleModel ?? null,
                    vTrim: null,
                    vColor: null,
                    vVin: null,
                    vMileage: null,
                    priceVehicle: "0",
                    downPayment: null,
                    tradeAllowance: null,
                    tradePayoff: null,
                    apr: null,
                    term: null,
                    amountDDue: payload.amountFinanced ? String(payload.amountFinanced) : null,
                    monthlypymts: payload.monthlyPayment ? String(payload.monthlyPayment) : null,
                    totalPayments: null,
                    totalFinanceCharges: null,
                    deal_created_at: payload.ts,
                };
                return [newDeal, ...prev];
            });
            setNewIds(prev => new Set(prev).add(payload.dealId));
            setTimeout(() => setNewIds(prev => { const s = new Set(prev); s.delete(payload.dealId); return s; }), 5000);
        };

        const onStatusUpdate = (payload: DealSocketPayload) => {
            setDeals(prev =>
                prev.map(d => d.deal_id === payload.dealId ? { ...d, deal_status: payload.status } : d)
            );
        };

        socket.on("newDeal", onNewDeal);
        socket.on("dealStatusUpdate", onStatusUpdate);
        return () => {
            socket.off("newDeal", onNewDeal);
            socket.off("dealStatusUpdate", onStatusUpdate);
        };
    }, [dealerRoom, dealerId]);

    const act = useCallback(async (dealId: number, status: string, counterOffer?: Partial<CounterState>) => {
        setActing(dealId);
        try {
            await updateDealStatus(dealId, status, dealerRoom, counterOffer as any);
            setDeals(prev => prev.map(d => d.deal_id === dealId ? { ...d, deal_status: status } : d));
            setExpanded(null);
        } catch (e) {
            console.error("updateDealStatus error", e);
        } finally {
            setActing(null);
        }
    }, [dealerRoom]);

    const setCounter = (dealId: number, field: keyof CounterState, value: string) => {
        setCounters(prev => ({
            ...prev,
            [dealId]: { ...(prev[dealId] ?? emptyCounter()), [field]: value },
        }));
    };

    const setDesk = (dealId: number, patch: Partial<typeof deskState[number]>) => {
        setDeskState(prev => ({ ...prev, [dealId]: { ...(prev[dealId] ?? { locking: false }), ...patch } }));
    };

    const submitAssign = async (dealId: number) => {
        const d = deskState[dealId] ?? {};
        await assignSalesperson(dealId, {
            salesperson1Id: d.sp1Id,
            salesperson1Name: d.sp1Name,
            salesperson2Id: d.sp2Id,
            salesperson2Name: d.sp2Name,
        });
    };

    const submitLock = async (dealId: number) => {
        const d = deskState[dealId] ?? { locking: false };
        setDesk(dealId, { locking: true });
        try {
            await deskDeal(dealId, { locked: true, selectedLenderId: d.lenderId, dealerRoom });
            setDeals(prev => prev.map(deal => deal.deal_id === dealId ? { ...deal, deal_locked: true } as any : deal));
            setDeskPanel(null);
        } finally {
            setDesk(dealId, { locking: false });
        }
    };

    const FILTERS = ["PENDING", "VIEWED", "COUNTERED", "ACCEPTED", "DECLINED", "ALL"];

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">Deal Desk</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Live incoming deals · New deals appear instantly via Socket.IO
                    </p>
                </div>
                {newIds.size > 0 && (
                    <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-400 px-3 py-1.5 rounded-full text-sm animate-pulse">
                        <Bell className="h-4 w-4" />
                        {newIds.size} new deal{newIds.size > 1 ? "s" : ""}
                    </div>
                )}
            </div>

            {/* Status filter tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1">
                {FILTERS.map(f => (
                    <button
                        key={f}
                        onClick={() => setStatusFilter(f)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                            statusFilter === f
                                ? "bg-indigo-500 text-white"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                        }`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            {deals.length === 0 ? (
                <div className="text-center py-20 space-y-4">
                    <Inbox className="h-12 w-12 mx-auto text-muted-foreground/30" />
                    <p className="text-muted-foreground">No deals in this queue.</p>
                    <p className="text-xs text-muted-foreground">New deals appear here in real time as customers submit them.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {deals.map(deal => {
                        const status = deal.deal_status ?? "PENDING";
                        const isNew = newIds.has(deal.deal_id);
                        const isExpanded = expanded === deal.deal_id;
                        const counter = counters[deal.deal_id] ?? emptyCounter();
                        const isActing = acting === deal.deal_id;
                        const isFinance = (deal.deal_type ?? "FINANCE") === "FINANCE";

                        return (
                            <Card
                                key={deal.deal_id}
                                className={`p-5 space-y-4 transition-all ${isNew ? "ring-2 ring-amber-400" : ""}`}
                            >
                                {/* Header */}
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            {isNew && <Bell className="h-4 w-4 text-amber-500 animate-bounce" />}
                                            <p className="font-medium">
                                                {deal.vYear ?? "?"} {deal.vMake} {deal.vModel}
                                                {deal.vTrim ? ` ${deal.vTrim}` : ""}
                                            </p>
                                        </div>
                                        {deal.vVin && (
                                            <p className="text-xs text-muted-foreground font-mono">VIN: {deal.vVin}</p>
                                        )}
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {new Date(deal.deal_created_at).toLocaleString()} · #{deal.deal_number || deal.deal_id}
                                        </p>
                                    </div>
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[status] ?? STATUS_COLORS.PENDING}`}>
                                        {status}
                                    </span>
                                </div>

                                {/* Deal numbers */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                    <Stat label="Vehicle Price" value={fmt(deal.priceVehicle)} />
                                    {isFinance ? (
                                        <>
                                            <Stat label="Monthly" value={deal.monthlypymts ? `${fmt(deal.monthlypymts)}/mo` : "—"} highlight />
                                            <Stat label="Amount Financed" value={fmt(deal.amountDDue)} />
                                            <Stat label="Term / APR" value={`${deal.term ?? "—"} mo @ ${deal.apr ?? "—"}%`} />
                                        </>
                                    ) : (
                                        <Stat label="Cash Due" value={fmt(deal.amountDDue)} highlight />
                                    )}
                                    {deal.downPayment && Number(deal.downPayment) > 0 && (
                                        <Stat label="Down Payment" value={fmt(deal.downPayment)} />
                                    )}
                                    {deal.tradeAllowance && Number(deal.tradeAllowance) > 0 && (
                                        <Stat label="Trade-In" value={fmt(deal.tradeAllowance)} />
                                    )}
                                </div>

                                {/* Action row */}
                                {(status === "PENDING" || status === "VIEWED") && (
                                    <div className="flex flex-wrap gap-2 pt-1 border-t">
                                        <Button
                                            size="sm"
                                            className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5"
                                            disabled={isActing}
                                            onClick={() => act(deal.deal_id, "ACCEPTED")}
                                        >
                                            {isActing ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                                            Accept
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-1.5"
                                            disabled={isActing}
                                            onClick={() => {
                                                setExpanded(isExpanded ? null : deal.deal_id);
                                                if (!counters[deal.deal_id]) setCounters(p => ({ ...p, [deal.deal_id]: emptyCounter() }));
                                                act(deal.deal_id, "VIEWED");
                                            }}
                                        >
                                            <RefreshCw className="h-3 w-3" />
                                            Counter
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-red-200 text-red-600 hover:bg-red-50 gap-1.5"
                                            disabled={isActing}
                                            onClick={() => act(deal.deal_id, "DECLINED")}
                                        >
                                            <XCircle className="h-3 w-3" />
                                            Decline
                                        </Button>
                                        {status === "PENDING" && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="text-muted-foreground gap-1.5"
                                                disabled={isActing}
                                                onClick={() => act(deal.deal_id, "VIEWED")}
                                            >
                                                <Clock className="h-3 w-3" />
                                                Mark Viewed
                                            </Button>
                                        )}
                                    </div>
                                )}

                                {/* Counter offer panel */}
                                {isExpanded && (
                                    <div className="border rounded-xl p-4 space-y-3 bg-violet-50 dark:bg-violet-950/20 border-violet-200 dark:border-violet-800">
                                        <p className="text-sm font-medium text-violet-800 dark:text-violet-300">Counter Offer Details</p>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            <div>
                                                <label className="text-xs font-medium uppercase text-muted-foreground mb-1 block">APR (%)</label>
                                                <Input
                                                    type="number" step="0.01" placeholder={deal.apr ?? "7.99"}
                                                    value={counter.apr}
                                                    onChange={e => setCounter(deal.deal_id, "apr", e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium uppercase text-muted-foreground mb-1 block">Term (mo)</label>
                                                <Input
                                                    type="number" placeholder={deal.term ?? "60"}
                                                    value={counter.term}
                                                    onChange={e => setCounter(deal.deal_id, "term", e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium uppercase text-muted-foreground mb-1 block">Monthly Pmt ($)</label>
                                                <Input
                                                    type="number" placeholder={deal.monthlypymts ?? "0"}
                                                    value={counter.monthlyPayment}
                                                    onChange={e => setCounter(deal.deal_id, "monthlyPayment", e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium uppercase text-muted-foreground mb-1 block">Amt Financed ($)</label>
                                                <Input
                                                    type="number" placeholder={deal.amountDDue ?? "0"}
                                                    value={counter.amountFinanced}
                                                    onChange={e => setCounter(deal.deal_id, "amountFinanced", e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex gap-2 pt-1">
                                            <Button
                                                size="sm"
                                                className="bg-violet-500 hover:bg-violet-600 text-white"
                                                disabled={isActing}
                                                onClick={() => act(deal.deal_id, "COUNTERED", counter)}
                                            >
                                                {isActing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                                Send Counter
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => setExpanded(null)}>Cancel</Button>
                                        </div>
                                    </div>
                                )}

                                {/* Accepted / Declined final state */}
                                {status === "ACCEPTED" && (
                                    <div className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4 shrink-0" />
                                        Deal accepted — customer has been notified.
                                    </div>
                                )}
                                {status === "DECLINED" && (
                                    <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-2">
                                        <XCircle className="h-4 w-4 shrink-0" />
                                        Deal declined — customer has been notified.
                                    </div>
                                )}

                                {/* Deal Desk panel toggle */}
                                <div className="border-t pt-3 flex items-center justify-between">
                                    <button
                                        onClick={() => setDeskPanel(deskPanel === deal.deal_id ? null : deal.deal_id)}
                                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition"
                                    >
                                        <Users className="h-3.5 w-3.5" />
                                        Desk this deal
                                    </button>
                                    {(deal as any).deal_locked && (
                                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                                            <Lock className="h-3 w-3" /> Locked
                                        </span>
                                    )}
                                </div>

                                {deskPanel === deal.deal_id && (() => {
                                    const ds = deskState[deal.deal_id] ?? { locking: false };
                                    return (
                                        <div className="bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-4">
                                            {/* Salesperson assignment */}
                                            <div>
                                                <p className="text-xs font-medium uppercase text-muted-foreground mb-2">Assign Salesperson(s)</p>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-xs text-muted-foreground mb-1 block">SP 1</label>
                                                        <select
                                                            className="w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800"
                                                            value={ds.sp1Id ?? ""}
                                                            onChange={e => {
                                                                const sp = salespeople.find(s => s.salesperson_id === Number(e.target.value));
                                                                setDesk(deal.deal_id, { sp1Id: sp?.salesperson_id, sp1Name: sp ? `${sp.salesperson_firstname ?? ""} ${sp.salesperson_lastname ?? ""}`.trim() : undefined });
                                                            }}
                                                        >
                                                            <option value="">— Select —</option>
                                                            {salespeople.map(s => (
                                                                <option key={s.salesperson_id} value={s.salesperson_id}>
                                                                    {s.salesperson_firstname} {s.salesperson_lastname}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs text-muted-foreground mb-1 block">SP 2 (optional)</label>
                                                        <select
                                                            className="w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800"
                                                            value={ds.sp2Id ?? ""}
                                                            onChange={e => {
                                                                const sp = salespeople.find(s => s.salesperson_id === Number(e.target.value));
                                                                setDesk(deal.deal_id, { sp2Id: sp?.salesperson_id, sp2Name: sp ? `${sp.salesperson_firstname ?? ""} ${sp.salesperson_lastname ?? ""}`.trim() : undefined });
                                                            }}
                                                        >
                                                            <option value="">— None —</option>
                                                            {salespeople.map(s => (
                                                                <option key={s.salesperson_id} value={s.salesperson_id}>
                                                                    {s.salesperson_firstname} {s.salesperson_lastname}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <Button size="sm" variant="outline" className="mt-2" onClick={() => submitAssign(deal.deal_id)}>
                                                    Save Assignment
                                                </Button>
                                            </div>

                                            {/* Lender selection + lock */}
                                            <div>
                                                <p className="text-xs font-medium uppercase text-muted-foreground mb-2">Route to Lender & Lock</p>
                                                <div className="flex gap-3 items-end">
                                                    <div className="flex-1">
                                                        <select
                                                            className="w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800"
                                                            value={ds.lenderId ?? ""}
                                                            onChange={e => setDesk(deal.deal_id, { lenderId: e.target.value ? Number(e.target.value) : undefined })}
                                                        >
                                                            <option value="">— Select Lender —</option>
                                                            {lenders.map(l => (
                                                                <option key={l.lender_id} value={l.lender_id}>{l.lender_name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
                                                        disabled={ds.locking || (deal as any).deal_locked}
                                                        onClick={() => submitLock(deal.deal_id)}
                                                    >
                                                        {ds.locking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />}
                                                        {(deal as any).deal_locked ? "Locked" : "Lock Deal"}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className={`font-medium mt-0.5 ${highlight ? "text-indigo-600 dark:text-indigo-400" : ""}`}>{value}</p>
        </div>
    );
}
